/* Shared by both frontends — they report into the same Sentry project, so the noise they have to
   drop is the same. Both reach it through a `shared` alias, of their bundler and of tsconfig
   respectively, which is why this stays plain JavaScript: webpack 4 has no loader for TypeScript. */

import {flatMap, startsWith, values} from './legacy';

/* Extensions carry their own protocol in the URLs they report, Safari masking its own as
   "webkit-masked-url://hidden/". `denyUrls` cannot match those: the stack parser knows no such
   protocol and splits the URL across the frame's filename and function, so both have to be
   checked. */
const ExtensionURLPattern = /(webkit-masked-url|safari-web-extension|chrome-extension|moz-extension):/i;

const isExtensionFrame = frame => (
    ExtensionURLPattern.test(frame.filename || '')
    || ExtensionURLPattern.test(frame.function || '')
);

/* An app that hosts us in a webview of its own writes a logger into the page, which reports under
   the protocol its in-app browser serves. That protocol is all that tells the script apart from
   ours: it names no host to deny, and the `Java object is gone` of a native bridge that outlived
   its Android object places it no better. */
const InAppBrowserURLPattern = /iabjs:/i;

const isInAppBrowserFrame = frame => InAppBrowserURLPattern.test(frame.filename || '');

const isThirdPartyFrame = frame => isExtensionFrame(frame) || isInAppBrowserFrame(frame);

/* WebKit throws objects that name their source file, and sentry attaches them as extra data. That
   is all an extension leaves behind when the browser reports its exception without a stack. */
const isExtensionSource = extra => Boolean(extra) && ExtensionURLPattern.test(extra.sourceURL || '');

const getValues = event => (event.exception && event.exception.values) || [];

const getFrames = event => flatMap(
    getValues(event),
    value => (value.stacktrace && value.stacktrace.frames) || [],
);

/* Third-party noise that runs from our own URL, so `denyUrls` cannot catch it. Every entry has to
   name something no code of ours would ever say — these match the message alone, with no stack to
   tell us where it came from. */
export const IgnoreErrors = [
    /* Benign rendering notification, not an actual error. */
    /ResizeObserver loop/,
    /* WebExtension APIs (browser./runtime. calls) injected by extensions. */
    /Invalid call to (browser|runtime)\./,
    /* Obfuscated code: terser names ours `t` and `e`, never `_0x4efa94`. */
    /_0x[0-9a-f]{4,}/,
    /* Page translation moves nodes out from under the framework rendering them. */
    'The object can not be found here',
    /* Password managers reading their own tab state. */
    'customFillData',
    /* Cashback extensions asking a response of ours for their reminder. */
    'cashbackReminder',
    /* Extensions replacing built-ins the engine will not let them replace, the user agent among
       them — they rewrite it to hide the browser. */
    /(Cannot|can't) redefine (non-configurable )?property/,
    /* The bridge an iOS host app puts into its webview, and what it answers when nothing is on the
       other end of it. */
    /webkit\.messageHandlers/,
    'WKWebView API client did not respond',
    /* Google's tag manager throws its own minified names. Ours are minified too, but a message of
       nothing but a letter or three is never one of ours: we throw sentences. */
    /^[A-Za-z]{1,3}$/,
    /* Safari's own wording for a message no extension came back to answer. */
    'TimeoutError: operation timed out',
    /* A rejection carrying neither message nor stack — there is nothing in it to look into. */
    'UnhandledRejection: {}',
    'Non-Error promise rejection captured',
    /* Both frontends cancel a request whose answer they no longer need — a search term that has
       been typed on, a query whose options changed — and the fetch rejects unhandled. */
    /AbortError/,
    /* The buyer submitted the PayPal form twice; the first submission had gone through. */
    'PAYMENT_ALREADY_DONE',
];

/* Third-party code we can recognise by where it is loaded from. */
export const DenyURLs = [
    /* Safari extensions carry their own bundle path, e.g. PayPal Honey. */
    /\.safariextension\//i,
    /* Extensions of the other browsers name their protocol, which the stack parser keeps intact. */
    /^(chrome|moz)-extension:/,
    /* Analytics we load but do not maintain. */
    /googletagmanager\.com|google-analytics\.com/,
];

export const isThirdPartyEvent = event => (
    getFrames(event).some(isThirdPartyFrame)
    || values(event.extra || {}).some(isExtensionSource)
);

/* The engine's own built-ins name no file to be placed by — `Object.keys` and `Array.map` report
   `<anonymous>` like anything else the page never loaded. But one of those throwing is us handing
   it something it cannot take, and that is ours to fix. They give themselves away by the line they
   do not have: whatever was injected into the page names one even with no file to place it in. */
const hasSourceLocation = frame => typeof frame.lineno === 'number';

/* A frame that names a file names where it was loaded from, and the inline scripts of our templates
   report the page they sit in. Anything else was put into the page from outside — by an extension,
   or by an app hosting us in its webview. Code run from a string would name no file either, but we
   run none: no eval, no new Function. */
const PlaceableFilenamePattern = /^[a-z-]+:\/\//i;

const isPlaceableFrame = frame => PlaceableFilenamePattern.test(frame.filename || '');

/* Only the innermost frame tells us whose the error is: the ones above it are ours by the time
   sentry wraps a handler, and they would vouch for a stack that merely passed through us. */
export const isInjectedStack = event => {
    const frames = getFrames(event).filter(hasSourceLocation);
    const innermost = frames[frames.length - 1];

    return Boolean(innermost) && !isPlaceableFrame(innermost);
};

export const matchesError = (event, pattern) => getValues(event).some(value => pattern.test(value.value || ''));

/* A page saved to disk keeps loading our scripts from our domain and reports their errors, but
   misses everything the template inlines. Nothing we could fix from here. */
export const isLocalCopy = event => startsWith((event.request && event.request.url) || '', 'file:');

/* Every script we serve is a `.js` file — the bundles, the translation catalog — and what the
   templates inline is assignments alone. Frames that name the document instead ran in a script the
   page was given from outside: a browser hosting us in a webview writes its own into it, and the
   engine then reports the document as their source, at a line and column of code the document never
   held. Where there are no frames at all there is nothing to place the error by, and the extra
   context below stands in for that. */
const ScriptFilePattern = /\.js$/i;

const isScriptFrame = frame => ScriptFilePattern.test(frame.filename || '');

const isInjectedIntoDocument = event => {
    const frames = getFrames(event);

    return frames.length > 0 && !frames.some(isScriptFrame);
};

/* Running out of stack is reported by Chrome on iOS and by the Google app, never by Mobile Safari
   on the same engine — those two inject scripts of their own, which is what the frames say where
   they name the document. Our own rendering could just as well be what runs out of room, so what
   arrives with no frames to place it by carries what the page held at the time instead. `__gCrWeb`
   is the bridge those two put on the window. */
const StackOverflowPattern = /Maximum call stack size exceeded/i;

const isStackOverflow = event => getValues(event).some(value => StackOverflowPattern.test(value.value || ''));

export const isInjectedStackOverflow = event => isStackOverflow(event) && isInjectedIntoDocument(event);

const describePage = () => ({
    elements: document.querySelectorAll('*').length,
    cells: document.querySelectorAll('td').length,
    body: document.body.className,
    /* eslint-disable-next-line no-underscore-dangle */
    injected: Boolean(window.__gCrWeb),
});

const isSyntaxError = event => getValues(event).some(value => value.type === 'SyntaxError');

/* The script that failed to parse is one of the injected ones. What our templates inline is
   generated from data, so a parse error of ours would break the page for every visitor rather than
   for the odd webview, and we would hear of it by other means. */
export const isInjectedSyntaxError = event => isSyntaxError(event) && isInjectedIntoDocument(event);

/* A script that fails to parse is reported without a stack and without a file, which leaves nothing
   to tell whose script it was. The scripts the document holds do tell: ours are there under their
   hashed names, and anything else came from the browser the visitor brought. Should one of ours be
   missing from a page that reports this, it is ours to fix after all. */
const isUnplaceableSyntaxError = event => isSyntaxError(event) && !getFrames(event).length;

const describeScripts = () => Array.prototype.map.call(
    document.scripts,
    script => script.src || '<inline>',
);

/* Both of these are errors we cannot place from the report alone, and no event is ever both. */
export const addExtraContext = event => {
    if (typeof document === 'undefined') return event;

    if (isStackOverflow(event)) {
        event.extra = {...event.extra, page: describePage()};
    } else if (isUnplaceableSyntaxError(event)) {
        event.extra = {...event.extra, scripts: describeScripts()};
    }

    return event;
};
